import {
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IConditionModel,
  IDisplayItem,
  ISymbol,
  IValidation
} from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import { getValidationMessage, resolveProjectLocale } from "./ai-localized-defaults";
import type { ScreenDesignInstance } from "./designer-context";
import { getScreenDictionary, getScreenDesign, safeParse } from "./designer-utils";
import { resolveEditTarget, type IScreenScopedInput } from "./detached-screen-session";
import { PropertyHelper } from "./property-helper";

const propertyHelper = new PropertyHelper();

/**
 * Summarize a condition for human-readable output.
 */
export function summarizeCondition(condition: IConditionModel | undefined): string | null {
  if (!condition) return null;

  if (condition.conditionSet?.conditions?.length) {
    const parts = condition.conditionSet.conditions.map((c) => {
      const field1Desc = c.field1?.diPropName || c.field1?.description || "field";
      const op = c.conditionOperator || "?";
      const field2Val = c.field2?.fixedValue !== undefined ? String(c.field2.fixedValue) : c.field2?.description || "";
      return field2Val ? `${field1Desc} ${op} ${field2Val}` : `${field1Desc} ${op}`;
    });
    const joiner = condition.conditionSet.type === ConditionSetType.or ? " OR " : " AND ";
    return parts.join(joiner);
  }

  if (condition.field1 && condition.conditionOperator) {
    const field1Desc = condition.field1.diPropName || condition.field1.description || "field";
    const op = condition.conditionOperator;
    const field2Val =
      condition.field2?.fixedValue !== undefined
        ? String(condition.field2.fixedValue)
        : condition.field2?.description || "";
    return field2Val ? `${field1Desc} ${op} ${field2Val}` : `${field1Desc} ${op}`;
  }

  return null;
}

/**
 * List all validation rules on a component.
 */
/** Target of a validation read/write: one component, on one screen. */
export interface IListComponentValidationsInput extends IScreenScopedInput {
  kuikaId?: string;
}

export interface IAddComponentValidationInput extends IScreenScopedInput {
  kuikaId?: string;
  validationType?: string;
  value?: any;
  message?: string;
  condition?: any;
}

export interface IUpdateComponentValidationInput extends IScreenScopedInput {
  kuikaId?: string;
  validationId?: string;
  message?: string;
  ignoreIfNotVisible?: any;
}

export interface IRemoveComponentValidationInput extends IScreenScopedInput {
  kuikaId?: string;
  validationId?: string;
}

export function listComponentValidations(sdInstance: ScreenDesignInstance, input: IListComponentValidationsInput): any {
  const sd = resolveEditTarget(sdInstance, input?.screenId);

  // Fallback: "show the validation rules on THIS component" often arrives with
  // no explicit kuikaId — resolve the currently-selected component on the canvas
  // instead of hard-erroring (mirrors ui_screen_query.get_selected).
  let kuikaId: string | undefined = input.kuikaId;
  if (!kuikaId) {
    const selectedDi: IDisplayItem | undefined = sd.getSelectedDisplayItem?.(getScreenDesign(sd));
    kuikaId = selectedDi?.id?.toString();
  }
  if (!kuikaId) {
    return { error: "kuikaId is required — pass a component id, or select a component on the canvas first." };
  }

  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId ${kuikaId} not found` };

  // Check if component supports validation
  const componentDefs = sdInstance.projectProvider.state.project?.componentSetObj?.components;
  const compDef = componentDefs?.find((c) => c.id === di.item.id);
  const hasValidate = compDef?.hasValidate === true;

  const validations = di.item?.validations || [];

  return {
    kuikaId,
    componentName: di.item?.name || "Unknown",
    hasValidate,
    validationCount: validations.length,
    validations: validations.map((v: IValidation) => ({
      id: v.id,
      message: v.message || "",
      conditionSummary: summarizeCondition(v.condition),
      ignoreIfNotVisible: v.ignoreIfNotVisible ?? false,
      hasValidationCondition: !!v.validationCondition
    }))
  };
}

/**
 * Build an ISymbol referencing a component's value property.
 */
function buildValuePropSymbol(diId: number | undefined, propName: string, valueType: string): ISymbol {
  return {
    description: `[diId:${diId}].${propName}`,
    category: ESymbolCategory.displayItemProp,
    label: "Property Of Component",
    diId,
    diPropName: propName,
    currentType: valueType,
    targetType: valueType,
    currentCardinality: ECardinality.One,
    targetCardinality: ECardinality.One
  } as ISymbol;
}

/**
 * Build a fixed-value ISymbol for field2 (comparison value).
 */
function buildFixedValueSymbol(value: any, valueType: string): ISymbol {
  return {
    category: ESymbolCategory.fixed,
    fixedValue: value,
    currentType: valueType,
    targetType: valueType,
    currentCardinality: ECardinality.One,
    targetCardinality: ECardinality.One
  } as ISymbol;
}

/**
 * Map a validation type string to the appropriate operator.
 */
function getOperatorForType(validationType: string): string {
  switch (validationType) {
    case "required":
      return "isNotEmpty";
    case "minLength":
      return "isLongerOrEqualLength";
    case "maxLength":
      return "isShorterOrEqualLength";
    case "minValue":
      return "isGreaterOrEqual";
    case "maxValue":
      return "isLessOrEqual";
    default:
      return "isNotEmpty";
  }
}

/**
 * Check if operator needs a field2 value.
 */
function operatorNeedsField2(operator: string): boolean {
  return operator !== "isNotEmpty" && operator !== "isEmpty";
}

export interface BuildComponentValidationInput {
  validationType: string;
  value?: string | number;
  message?: string;
  condition?: unknown;
}

interface BuiltComponentValidation {
  validation: IValidation;
}

interface ComponentValidationBuildError {
  error: string;
}

/**
 * Build a single `IValidation` for a component WITHOUT committing it to the
 * screen tree (no `di.item.validations` push, no `setScreenDesign`). This is
 * the shared no-commit core behind BOTH:
 *   - `addComponentValidation` (the `ui_validation.add` tool — pushes + commits)
 *   - `applyComponentValidations` (the markup `validate="..."` add path in
 *     component-tools.ts — pushes and lets the batch add's single commit persist)
 *
 * Returns the built validation, or a typed error (unsupported type, the
 * component lacks `hasValidate`, no value property, or a missing required
 * value). Callers decide whether an error is fatal (tool) or a soft skip
 * (markup add path).
 */
export function buildComponentValidation(
  sdInstance: ScreenDesignInstance,
  di: IDisplayItem,
  input: BuildComponentValidationInput
): BuiltComponentValidation | ComponentValidationBuildError {
  const validType = input.validationType;
  const validTypes = ["required", "minLength", "maxLength", "minValue", "maxValue", "custom"];
  if (!validTypes.includes(validType)) {
    return { error: `Invalid validationType "${validType}". Must be one of: ${validTypes.join(", ")}` };
  }

  // Check if component supports validation
  const componentDefs = sdInstance.projectProvider.state.project?.componentSetObj?.components;
  const compDef = componentDefs?.find((c) => c.id === di.item.id);
  if (!compDef?.hasValidate) {
    return { error: `Component "${di.item?.name}" does not support validations (hasValidate is false)` };
  }

  // Find value properties. A deprecated one (LocationSearch's compound `value`, KOA-18575) is no
  // longer part of the component's contract and is never validated.
  const valueProps = compDef.properties?.filter((x) => x.isValueProp === true && x.isDeprecated !== true);
  if (!valueProps || valueProps.length === 0) {
    return { error: `Component "${di.item?.name}" has no value properties to validate` };
  }

  const validationId = Guid.create().toString();

  // Default message — localized to the project's default language. An explicit
  // caller-supplied message always wins; otherwise the message is authored in
  // the app's language (the platform's phrase sweep translates it per-language
  // at save time, so raw default-language text is the correct thing to write).
  const locale = resolveProjectLocale(sdInstance);
  const message = input.message || getValidationMessage(validType, input.value, locale);

  const validation: IValidation = {
    id: validationId,
    isExpandedPanel: true,
    message
  } as IValidation;

  if (validType === "custom") {
    // For custom type, use the provided condition directly
    const parsedCondition = safeParse(input.condition);
    if (!parsedCondition) {
      return { error: 'For "custom" validationType, a condition object is required' };
    }
    validation.condition = parsedCondition as IConditionModel;
  } else {
    // Build condition based on validation type
    const operator = getOperatorForType(validType);
    const needsField2 = operatorNeedsField2(operator);

    if (needsField2 && (input.value === undefined || input.value === null)) {
      return { error: `validationType "${validType}" requires a value parameter` };
    }

    if (validType === "required") {
      // "required" creates isNotEmpty for every value prop (same as the UI panel): one condition
      // for a single-value component, an AND over all of them for a multi-value one.
      const conditions = valueProps.map((vp) => {
        const vt = propertyHelper.calculateBackEndValueType(vp);
        return {
          field1: buildValuePropSymbol(di.id, vp.name, vt),
          conditionOperator: "isNotEmpty"
        };
      });
      validation.condition = {
        id: Guid.create().toString(),
        conditionSet: {
          type: ConditionSetType.and,
          conditions
        }
      };
    } else {
      // minLength, maxLength, minValue, maxValue — apply to first value prop
      const valueProp = valueProps[0];
      const valueType = propertyHelper.calculateBackEndValueType(valueProp);

      const conditionEntry: IConditionModel = {
        field1: buildValuePropSymbol(di.id, valueProp.name, valueType),
        conditionOperator: operator
      };

      if (needsField2) {
        conditionEntry.field2 = buildFixedValueSymbol(input.value, valueType);
      }

      validation.condition = {
        id: Guid.create().toString(),
        conditionSet: {
          type: ConditionSetType.and,
          conditions: [conditionEntry]
        }
      };
    }
  }

  return { validation };
}

/**
 * Add a validation rule to a component (the `ui_validation.add` tool path).
 * Resolves the component, builds the validation via the shared no-commit core,
 * then pushes it and commits.
 */
export function addComponentValidation(sdInstance: ScreenDesignInstance, input: IAddComponentValidationInput): any {
  if (!input.kuikaId) return { error: "kuikaId is required" };
  if (!input.validationType) return { error: "validationType is required" };

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(input.kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId ${input.kuikaId} not found` };

  const built = buildComponentValidation(sdInstance, di, {
    validationType: input.validationType,
    value: input.value,
    message: input.message,
    condition: input.condition
  });
  if ("error" in built) return built;
  const { validation } = built;

  // Push and save
  if (!di.item.validations) {
    di.item.validations = [];
  }
  di.item.validations.push(validation);

  const screenDesign = getScreenDesign(sd);
  sd.setScreenDesign(screenDesign, ESdChangeReason.UpdateValidation);

  return {
    success: true,
    validationId: validation.id,
    message: `Added "${input.validationType}" validation to component ${input.kuikaId}`,
    validation: {
      id: validation.id,
      message: validation.message,
      conditionSummary: summarizeCondition(validation.condition),
      ignoreIfNotVisible: validation.ignoreIfNotVisible ?? false
    }
  };
}

/**
 * Update an existing validation rule.
 */
export function updateComponentValidation(
  sdInstance: ScreenDesignInstance,
  input: IUpdateComponentValidationInput
): any {
  if (!input.kuikaId) return { error: "kuikaId is required" };
  if (!input.validationId) return { error: "validationId is required" };

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(input.kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId ${input.kuikaId} not found` };

  const validations = di.item?.validations;
  if (!validations || validations.length === 0) {
    return { error: `Component ${input.kuikaId} has no validations` };
  }

  const validation = validations.find((v: IValidation) => v.id === input.validationId);
  if (!validation) {
    return { error: `Validation with id "${input.validationId}" not found on component ${input.kuikaId}` };
  }

  let updated = false;

  if (input.message !== undefined && input.message !== null) {
    validation.message = input.message;
    updated = true;
  }

  if (input.ignoreIfNotVisible !== undefined && input.ignoreIfNotVisible !== null) {
    const boolVal =
      input.ignoreIfNotVisible === true || input.ignoreIfNotVisible === "true" || input.ignoreIfNotVisible === 1;
    validation.ignoreIfNotVisible = boolVal;
    updated = true;
  }

  if (!updated) {
    return { error: "No fields to update. Provide message or ignoreIfNotVisible." };
  }

  const screenDesign = getScreenDesign(sd);
  sd.setScreenDesign(screenDesign, ESdChangeReason.UpdateValidation);

  return {
    success: true,
    message: `Updated validation "${input.validationId}" on component ${input.kuikaId}`,
    validation: {
      id: validation.id,
      message: validation.message,
      conditionSummary: summarizeCondition(validation.condition),
      ignoreIfNotVisible: validation.ignoreIfNotVisible ?? false
    }
  };
}

/**
 * Remove a validation rule from a component.
 */
export function removeComponentValidation(
  sdInstance: ScreenDesignInstance,
  input: IRemoveComponentValidationInput
): any {
  if (!input.kuikaId) return { error: "kuikaId is required" };
  if (!input.validationId) return { error: "validationId is required" };

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(input.kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId ${input.kuikaId} not found` };

  const validations = di.item?.validations;
  if (!validations || validations.length === 0) {
    return { error: `Component ${input.kuikaId} has no validations` };
  }

  const idx = validations.findIndex((v: IValidation) => v.id === input.validationId);
  if (idx === -1) {
    return { error: `Validation with id "${input.validationId}" not found on component ${input.kuikaId}` };
  }

  validations.splice(idx, 1);

  const screenDesign = getScreenDesign(sd);
  sd.setScreenDesign(screenDesign, ESdChangeReason.UpdateValidation);

  return {
    success: true,
    message: `Removed validation "${input.validationId}" from component ${input.kuikaId}`,
    remainingCount: validations.length
  };
}
